import cloneDeep from 'lodash/cloneDeep';
import { getModuleRowId, getModuleRowValue } from './moduleRowFields';

function normId(v) {
  if (v == null || v === '') {
    return null;
  }
  return String(v).trim().toLowerCase();
}

function normVal(v) {
  if (v == null || v === '') {
    return null;
  }
  return String(v).trim().toLowerCase();
}

/** Compare GUIDs ignoring braces and casing (API vs UI mismatch). */
function normGuid(v) {
  if (v == null || v === '') {
    return null;
  }
  return String(v).replace(/[{}]/g, '').trim().toLowerCase();
}

function moduleRowMatchesOverrideKey(mod, overrideKey) {
  const k = String(overrideKey).trim();
  const mid = getModuleRowId(mod);
  const mval = getModuleRowValue(mod);
  if (mid != null && mid !== '') {
    const ms = String(mid).trim();
    if (ms === k || normId(ms) === normId(k) || normGuid(ms) === normGuid(k)) {
      return true;
    }
  }
  if (mval != null && mval !== '') {
    const vs = String(mval).trim();
    if (vs === k || normVal(vs) === normVal(k)) {
      return true;
    }
  }
  return false;
}

/**
 * Patches `enableChat` / `EnableChat` on cloned `moduleInfo` rows for each
 * pending checkbox key. Does not depend on `customizeModule` matching shape —
 * only `pendingOverrides` keys vs server row id/value.
 *
 * @param {Array<object>} moduleInfo — from GET (normalized)
 * @param {Record<string, boolean>} pendingOverrides — stable keys from Chat AI checkboxes
 * @returns {Array<object>}
 */
export function applyPendingOverridesToModuleInfo(
  moduleInfo,
  pendingOverrides,
) {
  if (!Array.isArray(moduleInfo)) {
    return [];
  }
  const modules = cloneDeep(moduleInfo);
  const ov =
    pendingOverrides && typeof pendingOverrides === 'object'
      ? pendingOverrides
      : {};

  for (const [overrideKey, flag] of Object.entries(ov)) {
    const bool = Boolean(flag);
    for (let i = 0; i < modules.length; i++) {
      if (moduleRowMatchesOverrideKey(modules[i], overrideKey)) {
        modules[i] = {
          ...modules[i],
          enableChat: bool,
          EnableChat: bool,
        };
        break;
      }
    }
  }
  return modules;
}

/**
 * Builds the POST body for `postProjectSetting`, aligned with
 * [textContainerModule.js](src/modules/textContainerModule/textContainerModule.js).
 *
 * @param {object} projectData — normalized GET payload (`moduleInfo`, `groups`, `teamInfo`, etc.)
 * @param {string} operatorName — `teamSetting.operatorName`
 */
export function buildProjectSettingsSavePayload(projectData, operatorName) {
  const projectId =
    projectData?.projectInfo?.projectId ||
    projectData?.moduleInfo?.[0]?.projectId ||
    '';

  const defaultPermission = Array.isArray(projectData.groups)
    ? projectData.groups.map((group) => ({
        groupId: group.groupInfo.id,
        groupPermission: [],
      }))
    : [];

  const moduleSetting = [];
  if (Array.isArray(projectData.moduleInfo)) {
    for (const mod of projectData.moduleInfo) {
      const next = { ...mod };
      const ec =
        typeof next.enableChat === 'boolean'
          ? next.enableChat
          : typeof next.EnableChat === 'boolean'
          ? next.EnableChat
          : undefined;
      if (typeof ec === 'boolean') {
        next.enableChat = ec;
        next.EnableChat = ec;
      }
      if (next.groupPermissions) {
        moduleSetting.push(next);
      } else {
        moduleSetting.push({
          ...next,
          groupPermissions: defaultPermission,
        });
      }
    }
  }

  const groupSetting = Array.isArray(projectData.groups)
    ? projectData.groups.map((item) => {
        const next = { ...item };
        if (!next.groupInfo) {
          next.groupInfo = {};
        }
        next.groupInfo = { ...next.groupInfo, projectId };
        return next;
      })
    : [];

  return {
    clientSetting: projectData.clientInfo,
    groupSetting,
    projectSetting: projectData.projectInfo,
    teamSetting: {
      members: [
        projectData.teamInfo?.lead,
        projectData.teamInfo?.manager,
        ...(projectData.teamInfo?.members || []),
      ].filter(Boolean),
      operatorName,
    },
    moduleSetting,
    isKeepAnalysis: false,
    isKeepValueDirver: false,
  };
}

export { getModuleRowId, getModuleRowValue };
